import {type Observable, Subject, lastValueFrom} from 'rxjs';
import {map} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import type {BaseCompositeDataResult} from '../dtos/data-result-composite';
import type {Mitarbeiter} from '../dtos/mitarbeiter';
import type {TransformationInput} from '../dtos/transformation/transformation-input';
import type {TransformationMitarbeiter} from '../dtos/transformation/transformation-mitarbeiter';
import type {TransformedMitarbeiter} from '../dtos/transformation/transformed-mitarbeiter';
import type {AdditionalInfo} from '../dtos/wizard/additional-info';
import {DataStorageService} from './data-storage.service';
import {DataValidationService} from '../../analysis/analysis-shared/data-verification/data-validation.service';
import type {
  ValidationWorkerOutputProgress,
  ValidationWorkerOutputResult
} from '../../analysis/analysis-shared/data-verification/validation-worker-data';
import {
  ObHttpApiInterceptorEvents,
  ObNotificationService,
  ObSpinnerService
} from '@oblique/oblique';
import {calculateDataSummary} from '../../analysis/analysis-shared/data-verification/validation-functions';
import {MatDialog} from '@angular/material/dialog';
import {ProgressDialogComponent} from '../components/progress-dialog/progress-dialog.component';
import {sleep} from '../utils';

const DIALOG_DISPLAY_LIMIT = 5000;
@Injectable({
  providedIn: 'root'
})
export class WizardService {
  public readonly API_URL = 'api/datatransformation';

  // eslint-disable-next-line @typescript-eslint/member-ordering
  private readonly resultChangedSource = new Subject<BaseCompositeDataResult>();

  // eslint-disable-next-line @typescript-eslint/member-ordering
  resultChanged = this.resultChangedSource.asObservable();

  // eslint-disable-next-line @typescript-eslint/max-params
  constructor(
    private readonly storage: DataStorageService,
    private readonly validation: DataValidationService,
    private readonly interceptor: ObHttpApiInterceptorEvents,
    private readonly dialog: MatDialog,
    private readonly notification: ObNotificationService,
    private readonly spinner: ObSpinnerService,
    private readonly http: HttpClient
  ) {}

  public publishNewResult(result: BaseCompositeDataResult) {
    this.resultChangedSource.next(result);
  }

  async transformAndValidateMitarbeiters(): Promise<ValidationWorkerOutputResult> {
    const transformiert = await this.storage.mitarbeitersAreTransformed();
    if (transformiert) {
      const mitarbeiters =
        (await this.storage.getMitarbeiter()) as Mitarbeiter[];
      if (mitarbeiters[0].summaryStatus) {
        return Promise.resolve({
          mitarbeiters,
          statusFigures: calculateDataSummary(mitarbeiters)
        } as ValidationWorkerOutputResult);
      }
    }
    const status = this.validation.getStatus();
    let mitarbeiters = (await this.storage.getMitarbeiter()) as Mitarbeiter[];
    const additionalInfo = await this.storage.getAdditionalInfo();
    const ref = this.startProgressElement(
      status,
      mitarbeiters.length > DIALOG_DISPLAY_LIMIT
    );
    if (!transformiert) {
      mitarbeiters = await this.performDataTransformation(mitarbeiters);
    }
    const result = await this.validation.performValidation(
      mitarbeiters,
      additionalInfo,
      await this.storage.getIsForModule2()
    );
    await sleep(0);
    await this.storage.storeMitarbeiter(mitarbeiters, true);
    if (ref) {
      ref.close();
      this.notification.info({
        title: 'm1+2.sprint11.datenpruefen.validation.done-notification.title',
        message:
          'm1+2.sprint11.datenpruefen.validation.done-notification.content',
        sticky: true
      });
    } else {
      this.spinner.deactivate();
    }
    return result;
  }

  startProgressElement(
    status: Observable<ValidationWorkerOutputProgress>,
    useDialog: boolean
  ) {
    if (useDialog) {
      return this.dialog.open(ProgressDialogComponent, {
        width: '40%',
        disableClose: true,
        data: status
      });
    }
    this.spinner.activate();
    return null;
  }

  removeBknBstLeadingZeroes(ma: Mitarbeiter) {
    ma.betrieblichesKompetenzniveau = this.tryRemoveLeadingZeroes(
      ma.betrieblichesKompetenzniveau
    );
    ma.beruflicheStellung = this.tryRemoveLeadingZeroes(ma.beruflicheStellung);
  }

  tryRemoveLeadingZeroes(input: string | number): string | number {
    if (typeof input === 'string') {
      const result = parseInt(input, 10);
      if (!isNaN(result)) {
        return result;
      }
    }
    return input;
  }

  private async performDataTransformation(
    mitarbeiters: Mitarbeiter[]
  ): Promise<Mitarbeiter[]> {
    const additionalInfo = await this.storage.getAdditionalInfo();
    this.interceptor.deactivateSpinnerOnNextAPICalls(1);
    // Perform transformation server-side
    return lastValueFrom(
      this.http
        .post<
          TransformedMitarbeiter[]
        >(this.API_URL, this.getTransformationInput(additionalInfo, mitarbeiters))
        .pipe(
          map((transformedMitarbeiters: TransformedMitarbeiter[]) => {
            // Merges partial mitarbeiter data with client-side full mitarbeiter data
            transformedMitarbeiters.forEach(transformedMitarbeiter => {
              const theMitarbeiter = mitarbeiters.find(
                m => m.id === transformedMitarbeiter.id
              );
              if (theMitarbeiter) {
                this.fuseMitarbeiter(theMitarbeiter, transformedMitarbeiter);
              } else {
                console.error('No transformed result for', theMitarbeiter);
              }
              this.removeBknBstLeadingZeroes(theMitarbeiter);
            });
            return mitarbeiters;
          })
        )
    );
  }

  private getTransformationInput(
    additionalInfo: AdditionalInfo,
    mitarbeiters: Mitarbeiter[]
  ): TransformationInput {
    return {
      mitarbeiter: this.prepareForTransformation(mitarbeiters),
      ageOption: additionalInfo.alterConfig,
      yearsOfServiceOption: additionalInfo.dienstjahreConfig,
      genderCodeMale: additionalInfo.codierungMann,
      genderCodeFemale: additionalInfo.codierungFrau,
      referenceMonth: additionalInfo.referenzMonat,
      referenceYear: additionalInfo.referenzJahr
        ? additionalInfo.referenzJahr.toString()
        : additionalInfo.referenzJahr,
      buwaz: additionalInfo.betriebsueblicheArbeitszeitImReferenzMonat
    };
  }

  private prepareForTransformation(
    mitarbeiters: Mitarbeiter[]
  ): TransformationMitarbeiter[] {
    return mitarbeiters.map<TransformationMitarbeiter>(
      ma =>
        ({
          id: ma.id,
          alter: ma.alter,
          dienstjahre: ma.dienstjahre,
          geschlecht: ma.geschlecht,
          grundgesamtheit: ma.grundgesamtheit,
          betrieblicheWoechentlicheArbeitszeit:
            ma.betrieblicheWoechentlicheArbeitszeit
        }) as TransformationMitarbeiter
    );
  }

  private fuseMitarbeiter(
    mitarbeiter: Mitarbeiter,
    transformedMitarbeiter: TransformedMitarbeiter
  ) {
    Object.keys(transformedMitarbeiter).forEach(propName => {
      if (
        transformedMitarbeiter[propName] !== null &&
        transformedMitarbeiter[propName] !== undefined
      ) {
        mitarbeiter[propName] = transformedMitarbeiter[propName];
      }
    });
  }
}
